<template>
  <AuthLayout>
    <template #form>
      <div class="reset-password-form">
        <div class="reset-password-content">
          <!-- Logo and Title Section -->
          <AuthFormHeader
            variant="section"
            title="Create a new password"
            subtitle="Enter a strong password you haven't used before."
          />

          <!-- Form -->
          <div class="auth-form">
            <div>
              <an-input
                label="New password"
                type="password"
                v-model="password"
                :invalid="!passwordValid"
                placeholder="Password"
                :theme="theme"
                size="lg"
                focus
              />
              <div v-if="!passwordValid" class="auth-field-error">Password must contain at least 8 characters</div>
            </div>

            <div>
              <an-input
                label="Confirm new password"
                type="password"
                v-model="confirmPassword"
                :invalid="!confirmPasswordValid"
                placeholder="Password"
                :theme="theme"
                size="lg"
              />
              <div v-if="(!confirmPasswordValid || serverError) && errorMessage" class="auth-field-error">
                {{ errorMessage }}
              </div>
            </div>

            <an-button @click="submit" variant="purple" size="lg" :isWorking="isWorking" :rounded="false">
              Reset password
            </an-button>

            <an-link @click="navigateToLogin" class="auth-link-secondary"> Return to log in </an-link>
          </div>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import api from '@/api';
import { getRecaptchaToken } from '@/utils/recaptcha';

export default {
  data() {
    return {
      password: '',
      confirmPassword: '',
      passwordValid: true,
      confirmPasswordValid: true,
      showButton: false,
      errorMessage: '',
      serverError: false,
      isWorking: false,
      theme: 'dark'
    };
  },
  components: {
    AuthLayout,
    AuthFormHeader
  },
  mounted() {
    this.applyTheme(this.theme, 'onboarding');
  },
  methods: {
    validatePassword() {
      this.passwordValid = this.password?.length >= 8;
    },
    validateConfirmation() {
      this.confirmPasswordValid = this.confirmPassword === this.password;
      if (!this.passwordValid) {
        this.errorMessage = '';
      } else if (this.confirmPassword !== this.password) {
        this.errorMessage = 'Is this the same password though?';
      }
    },
    isValid() {
      this.validatePassword();
      this.validateConfirmation();
      return this.passwordValid && this.confirmPasswordValid;
    },
    async submit() {
      try {
        this.isWorking = true;
        this.serverError = false;
        const { password } = this;
        const { token } = this.$route.params;

        this.$trackEvent('reset-password.reset-password-button.click');

        if (this.isValid()) {
          // Get reCAPTCHA token
          const captchaToken = await getRecaptchaToken('reset_password');

          await api.post('/recover', {
            token,
            password,
            captcha_token: captchaToken
          });
          this.$trackEvent('reset-password.reset-password.success');
          this.$router.replace({ name: 'login', query: this.$route.query });
        }
      } catch (err) {
        const { response } = err;
        const message = response?.data?.message || "We couldn't save your changes...";
        this.errorMessage = message;
        this.serverError = true;
        this.confirmPasswordValid = false;
        this.$trackEvent('reset-password.reset-password.failure');
      } finally {
        this.isWorking = false;
      }
    },
    navigateToLogin() {
      this.$trackEvent('forgot-password.navigate-to-login.click');
      this.$router.replace({ name: 'login', query: this.$route.query });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.reset-password-form {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.reset-password-content {
  display: flex;
  flex-direction: column;
  gap: $auth-gap-md;
  padding: 0 $auth-padding-horizontal;
}

// ResetPassword-specific styles
.auth-field-error {
  margin-top: $auth-gap-xs;
  text-align: left;
  font-size: $auth-text-size;
  color: var(--red);
}

// All other shared styles moved to _auth.scss
</style>
